import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { siteRepository, createSitePort } from '@/lib/container'
import { useAuthStore } from '@/store/authStore'
import type { CreateSiteDTO } from '@/application/dto'
import type { SiteSettings } from '@brix/types'

export function useSites() {
  const tenant = useAuthStore((s) => s.tenant)
  return useQuery({
    queryKey: ['sites', tenant?.id],
    queryFn: () => siteRepository.findByTenantId(tenant!.id),
    enabled: !!tenant,
  })
}

export function useSite(siteId: string) {
  return useQuery({
    queryKey: ['site', siteId],
    queryFn: () => siteRepository.findById(siteId),
    enabled: !!siteId,
  })
}

export function useUpdateSiteSettings() {
  const qc = useQueryClient()
  const tenant = useAuthStore((s) => s.tenant)
  return useMutation({
    mutationFn: ({ id, settings }: { id: string; settings: SiteSettings }) =>
      siteRepository.update(id, { settings } as never),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ['sites', tenant?.id] })
      void qc.invalidateQueries({ queryKey: ['site', id] })
    },
  })
}

export function useCreateSite() {
  const qc = useQueryClient()
  const tenant = useAuthStore((s) => s.tenant)

  return useMutation({
    mutationFn: (dto: CreateSiteDTO) =>
      createSitePort.execute({ tenantId: tenant!.id, ...dto }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites', tenant?.id] }),
  })
}

export function useDeleteSite() {
  const qc = useQueryClient()
  const tenant = useAuthStore((s) => s.tenant)

  return useMutation({
    mutationFn: (id: string) => siteRepository.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites', tenant?.id] }),
  })
}

export function useUpdateSiteCustomDomain() {
  const qc = useQueryClient()
  const tenant = useAuthStore((s) => s.tenant)
  return useMutation({
    mutationFn: ({ id, customDomain }: { id: string; customDomain: string | null }) =>
      siteRepository.update(id, { customDomain } as never),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ['sites', tenant?.id] })
      void qc.invalidateQueries({ queryKey: ['site', id] })
    },
  })
}

export function useToggleSitePublished() {
  const qc = useQueryClient()
  const tenant = useAuthStore((s) => s.tenant)

  return useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      siteRepository.update(id, { published }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites', tenant?.id] }),
  })
}
